import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { logout } from 'src/auth';
import { resetUser } from 'src/auth/utils/resetUser';
import { NetworkState } from 'src/constants/network-state';
import AbilityRoleApiService from 'src/services/switch-rest-api/rest-ability-role.service';
import { SessionStorageTtlStore } from 'src/utils/sessionStorageTtlStore';
import { AbilityRole } from 'switch-shared/modules/crud/ability-role/ability-role.entity';
import { TtlCache } from 'switch-shared/utils/ttl-cache';

const abilityRoleApiService = new AbilityRoleApiService();

const ABILITY_ROLE_CACHE_TTL_MS = 2 * 60 * 1000;

const abilityRoleCache = new TtlCache<AbilityRole[]>({
  ttlMs: ABILITY_ROLE_CACHE_TTL_MS,
  store: new SessionStorageTtlStore<AbilityRole[]>(
    'admin-console:ability-roles:v1'
  ),
});

const CACHE_SLOT = 'current';

export const readAbilityRoleCache = (userId: string): AbilityRole[] | null =>
  abilityRoleCache.get(CACHE_SLOT, { scope: userId }) ?? null;

const writeAbilityRoleCache = (userId: string, roles: AbilityRole[]) => {
  abilityRoleCache.set(CACHE_SLOT, roles, { scope: userId });
};

const clearAbilityRoleCache = () => {
  abilityRoleCache.clear();
};

interface AbilityRoleInterface {
  status: NetworkState;
  abilityRoles: AbilityRole[];
  error: string | undefined;
  requestUserId: string | undefined;
}

const initialState: AbilityRoleInterface = {
  abilityRoles: [],
  status: NetworkState.NOT_STARTED,
  error: undefined,
  requestUserId: undefined,
};

export const fetchAbilityRole = createAsyncThunk(
  'ability-role/fetchAbilityRole',
  async (userId: string) => {
    try {
      const res = await abilityRoleApiService.getAbilityRole({});
      return res?.value;
    } catch (error: any) {
      throw new Error(error.message, { cause: error });
    }
  }
);

const abilityRoleSlice = createSlice({
  name: 'ability-role',
  initialState,
  reducers: {
    hydrateAbilityRolesFromCache(state, action: PayloadAction<AbilityRole[]>) {
      state.abilityRoles = action.payload;
      state.status = NetworkState.SUCCESS;
      state.error = undefined;
      state.requestUserId = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(resetUser.rejected, (state, action) => {
        state.status = NetworkState.ERROR;
        state.abilityRoles = [];
        state.error = action.error.message;
        state.requestUserId = undefined;
        clearAbilityRoleCache();
      })
      .addCase(fetchAbilityRole.pending, (state, action) => {
        state.status = NetworkState.LOADING;
        state.requestUserId = action.meta.arg;
      })
      .addCase(fetchAbilityRole.fulfilled, (state, action) => {
        if (state.requestUserId !== action.meta.arg) return;

        state.abilityRoles = action.payload;
        state.status = NetworkState.SUCCESS;
        state.error = undefined;
        state.requestUserId = undefined;
        writeAbilityRoleCache(action.meta.arg, action.payload);
      })
      .addCase(fetchAbilityRole.rejected, (state, action) => {
        if (state.requestUserId !== action.meta.arg) return;

        if (action.meta.aborted) {
          state.status = NetworkState.NOT_STARTED;
          state.requestUserId = undefined;
          return;
        }

        state.status = NetworkState.ERROR;
        state.requestUserId = undefined;
        clearAbilityRoleCache();
        /*
          TODO: Instead of routing to login if get ability roles fail, it should login but with no permissions
        */
        logout();

        toast.error(
          'There has been an error getting role details. Please try logging in'
        );
      })
      .addCase(resetUser.fulfilled, () => {
        clearAbilityRoleCache();
        return initialState;
      });
  },
});

export default abilityRoleSlice.reducer;
export const { hydrateAbilityRolesFromCache } = abilityRoleSlice.actions;
